import { gql } from '@apollo/client';
import type { DriverNames, UUID } from '@cobliteam/common-types';
import type {
  AddDriverMutation,
  BatchCreateDriverItemInput,
  BatchCreateDriversMutation,
  DriverNamesQuery,
  DriversWithIdentifiersQuery,
  DriversWithVehiclesQuery,
  UpdateDriverMutation,
} from '../../__generated__/graphql';
import graphQLRequester from '../../request/GraphQLRequester';
import type { GraphQLRestDataSourceError } from '../../request/Types';
import { driverGraphQLAPIToDriver, driverGraphQLAPIToDriverNames, driverToSaveDriverGraphQLAPI, newDriverToAddDriverGraphQLAPI } from './APITransformer';
import type { Driver, NewDriver } from './Types';

/**
 * `drivers` is `[Driver]` in the schema, so codegen types every element as nullable. Filtering here
 * keeps the transformers free of null checks.
 */
const presentDrivers = <T>(drivers: Array<T | null>): T[] => drivers.filter((driver): driver is T => driver != null);

export class GraphQLDriversRequester {
  public getDrivers = (): Promise<Driver[]> =>
    graphQLRequester
      .query<DriversWithIdentifiersQuery>({
        query: gql`
          query DriversWithIdentifiers {
            drivers {
              id
              name
              fleet_id
              assigned
              phone_number
              first_license_acquirement_date
              license_expiration_date
              license_category
              driver_code
              identifier
              identification_token {
                id
                alias
                tokenId
                type
                status
                isExternal
                fleetId
                driverId
              }
              cpf
              driver_license
              registry_id
              profile_image_url
            }
          }
        `,
      })
      .then((result) => presentDrivers(result.drivers).map(driverGraphQLAPIToDriver));

  public getDriversWithVehicles = (): Promise<Driver[]> =>
    graphQLRequester
      .query<DriversWithVehiclesQuery>({
        query: gql`
          query DriversWithVehicles {
            drivers {
              id
              name
              fleet_id
              assigned
              phone_number
              first_license_acquirement_date
              license_expiration_date
              license_category
              driver_code
              identification_token {
                id
                alias
                tokenId
                type
                status
                isExternal
                fleetId
                driverId
              }
              cpf
              driver_license
              registry_id
              profile_image_url
              vehicle {
                id
                licensePlate
                device {
                  id
                }
              }
            }
          }
        `,
      })
      .then((result) => presentDrivers(result.drivers).map(driverGraphQLAPIToDriver));

  public getDriverNames = (): Promise<DriverNames> =>
    graphQLRequester
      .query<DriverNamesQuery>({
        query: gql`
          query DriverNames {
            drivers {
              id
              name
            }
          }
        `,
      })
      .then((result) => driverGraphQLAPIToDriverNames(presentDrivers(result.drivers)));

  public deleteDriver = (driverId: UUID): Promise<UUID> => {
    const driverToDelete = { id: driverId };
    return graphQLRequester
      .mutate({
        mutation: gql`
          mutation DeleteDriver($driverToDelete: DeleteDriver) {
            deleteDriver(input: $driverToDelete) {
              id
            }
          }
        `,
        variables: { driverToDelete },
      })
      .then(() => driverToDelete.id);
  };

  public saveDriver = (driver: Driver | NewDriver): Promise<Driver> => {
    if ('id' in driver && driver.id) {
      return graphQLRequester
        .mutate<UpdateDriverMutation>({
          mutation: gql`
            mutation UpdateDriver($driverToUpdate: UpdateDriver) {
              updateDriver(input: $driverToUpdate) {
                id
                name
                fleet_id
                assigned
                phone_number
                first_license_acquirement_date
                license_expiration_date
                license_category
                driver_code
                cpf
                driver_license
                registry_id
                profile_image_url
              }
            }
          `,
          variables: {
            driverToUpdate: driverToSaveDriverGraphQLAPI(driver),
          },
        })
        .then((graphQlResponse) => {
          if (!graphQlResponse?.updateDriver) {
            throw new Error('Failed to update driver: No response data received');
          }
          return driverGraphQLAPIToDriver(graphQlResponse.updateDriver);
        });
    }
    return graphQLRequester
      .mutate<AddDriverMutation>({
        mutation: gql`
          mutation AddDriver($driverToAdd: AddDriver) {
            addDriver(input: $driverToAdd) {
              id
              name
              fleet_id
              assigned
              phone_number
              first_license_acquirement_date
              license_expiration_date
              license_category
              driver_code
              profile_image_url
            }
          }
        `,
        variables: {
          driverToAdd: newDriverToAddDriverGraphQLAPI(driver),
        },
      })
      .then((graphQlResponse) => {
        if (!graphQlResponse?.addDriver) {
          throw new Error('Failed to add driver: No response data received');
        }
        return driverGraphQLAPIToDriver(graphQlResponse.addDriver);
      });
  };

  public createDriversBatch = (drivers: BatchCreateDriverItemInput[]): Promise<Driver[]> => {
    return graphQLRequester
      .mutate<BatchCreateDriversMutation & { errors?: Array<GraphQLRestDataSourceError> }>({
        mutation: gql`
          mutation BatchCreateDrivers($input: BatchCreateDriversInput!) {
            batchCreateDrivers(input: $input) {
              id
              name
              fleet_id
              phone_number
              cpf
              driver_license
              registry_id
              license_category
              first_license_acquirement_date
              license_expiration_date
            }
          }
        `,
        variables: {
          input: { drivers },
        },
      })
      .then((graphQlResponse) => {
        // `parseGraphQLResponse` only rejects when the whole `data` is missing, so a field-level
        // error that nulls `batchCreateDrivers` still resolves. Falling back to `[]` there would
        // report a failed import as "0 drivers created" — same guard the operational-situation and
        // face-cluster requesters already use.
        if (graphQlResponse.errors?.length) {
          return Promise.reject<Driver[]>({ errors: graphQlResponse.errors });
        }
        return presentDrivers(graphQlResponse.batchCreateDrivers ?? []).map(driverGraphQLAPIToDriver);
      });
  };
}

export default GraphQLDriversRequester;
